import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Download, CloudUpload, Inventory2, Sync, Publish, Receipt, LocalShipping, ReceiptLong, Print } from '@mui/icons-material';
import { RootState } from '../store';
import ResponsiveDialog from '../components/common/ResponsiveDialog';
import apiService from '../services/api.service';
import { parseGs1, scanDedupeKey, toGtin14 } from '../utils/gs1Parser';
import { ITEM_MASTER_SEED } from '../data/itemMasterSeed';
import {
  loadEntries,
  putEntry,
  deleteEntry as dbDeleteEntry,
  clearEntries,
  enqueueRetry,
  loadRetryQueue,
  dequeueRetry,
  localId,
} from '../utils/scanDb';
import { exportXlsx, exportCsv } from '../utils/scanExport';
import ScannerView, { ScanKind } from '../components/warehouse/ScannerView';
import SourceDocumentPicker from '../components/warehouse/SourceDocumentPicker';
import ScanReviewCard from '../components/warehouse/ScanReviewCard';
import SessionTable from '../components/warehouse/SessionTable';
import ResolveQueueDialog from '../components/warehouse/ResolveQueueDialog';
import DocumentProgress from '../components/warehouse/DocumentProgress';
import type { ScanEntry, WarehouseMode, SourceDocHeader, ResolvedItem, ScanSessionMeta, ScanResolution, BinLocation, GrpoDoc, DeliveryDoc, DeliveryStockRow } from '../types/warehouse';

type Toast = { severity: 'success' | 'info' | 'warning' | 'error'; message: string } | null;

type SeedItem = { itemNo: string; itemDescription: string; itemGroup: string; pic: string; barcode: string; gtin: string };
const SEED_CACHE_KEY = 'warehouseItemSeedCache';

// The maintained item seed (from the Item Seed page) is the last-resort resolver.
// Online resolution uses it server-side; here it's cached for OFFLINE use, falling
// back to the bundled snapshot when no cache exists yet.
let SEED_CACHE: SeedItem[] = (() => {
  try {
    const raw = localStorage.getItem(SEED_CACHE_KEY);
    if (raw) return JSON.parse(raw) as SeedItem[];
  } catch {
    /* ignore */
  }
  return ITEM_MASTER_SEED as SeedItem[];
})();

function setSeedCache(rows: SeedItem[]): void {
  SEED_CACHE = rows.length ? rows : (ITEM_MASTER_SEED as SeedItem[]);
  try {
    localStorage.setItem(SEED_CACHE_KEY, JSON.stringify(SEED_CACHE));
  } catch {
    /* quota / private mode — keep the in-memory copy */
  }
}

/** Offline fallback resolution against the maintained seed (cached) or the bundled snapshot. */
function resolveFromSeed(barcode?: string, gtin?: string): ResolvedItem | null {
  const g14 = gtin ? toGtin14(gtin) : '';
  const row = SEED_CACHE.find((r) => {
    if (barcode && r.barcode && r.barcode === barcode) return true;
    if (gtin && r.gtin && (r.gtin === gtin || toGtin14(r.gtin) === g14)) return true;
    if (barcode && r.gtin && toGtin14(r.gtin) === toGtin14(barcode)) return true;
    return false;
  });
  return row ? { itemNo: row.itemNo, itemDescription: row.itemDescription, itemGroup: row.itemGroup, pic: row.pic, source: 'seed' } : null;
}

export default function WarehouseReceiving(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user) as any;
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || ''), []);
  const userName = String(user?.username || user?.fullname || user?.userid || 'unknown');

  const [mode, setMode] = useState<WarehouseMode>('Receiving');
  const [sourceDoc, setSourceDoc] = useState<SourceDocHeader | null>(null);
  const [entries, setEntries] = useState<ScanEntry[]>([]);
  const [pendingBarcode, setPendingBarcode] = useState<string | null>(null);
  const [draft, setDraft] = useState<ScanEntry | null>(null);
  const [resolveEntry, setResolveEntry] = useState<ScanEntry | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [importing, setImporting] = useState(false);
  const [startedAt] = useState<string>(() => new Date().toISOString());
  const [bins, setBins] = useState<BinLocation[]>([]);
  const [grpoOpen, setGrpoOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [grpos, setGrpos] = useState<GrpoDoc[]>([]);
  const [viewGrpoOpen, setViewGrpoOpen] = useState(false);
  const [grpoDetails, setGrpoDetails] = useState<GrpoDoc[]>([]);
  const [grpoDetailLoading, setGrpoDetailLoading] = useState(false);
  // Delivery (mode === 'Delivery'): mirror of the GRPO submit + viewer.
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [createInvoice, setCreateInvoice] = useState(true);
  const [deliveries, setDeliveries] = useState<DeliveryDoc[]>([]);
  const [viewDeliveryOpen, setViewDeliveryOpen] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDoc[]>([]);
  const [deliveryDetailLoading, setDeliveryDetailLoading] = useState(false);
  const [deliveryStock, setDeliveryStock] = useState<DeliveryStockRow[]>([]);
  const [printingKey, setPrintingKey] = useState<string | null>(null);

  // Load persisted session on mount.
  useEffect(() => {
    void loadEntries().then(setEntries);
  }, []);

  // Refresh the offline seed cache from the maintained Item Seed list (best-effort).
  useEffect(() => {
    apiService
      .listItemSeed()
      .then((res: any) => {
        if (Array.isArray(res?.data)) setSeedCache(res.data as SeedItem[]);
      })
      .catch(() => {
        /* offline — keep the existing cache / bundled snapshot */
      });
  }, []);

  // Entries scoped to the current mode.
  const modeEntries = useMemo(() => entries.filter((e) => e.mode === mode), [entries, mode]);
  const needsMappingCount = modeEntries.filter((e) => e.resolution === 'unresolved' || e.resolution === 'created_pending').length;

  // -------- bin locations for the selected document's warehouse(s) --------
  // Loaded for BOTH modes: Receiving puts goods into a bin; Delivery (goods issue
  // from a bin-managed warehouse) MUST allocate each line to a bin or SAP rejects
  // it ("-10 Fully allocate item to bin locations").
  useEffect(() => {
    if (!sourceDoc) {
      setBins([]);
      return;
    }
    const warehouses = Array.from(
      new Set(sourceDoc.lines.map((l) => l.warehouseCode).filter((w): w is string => !!w))
    );
    if (warehouses.length === 0) {
      setBins([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      warehouses.map((w) =>
        apiService
          .getWarehouseBins(companyId, w)
          .then((r: any) => (Array.isArray(r?.data) ? (r.data as BinLocation[]) : []))
          .catch(() => [] as BinLocation[])
      )
    ).then((results) => {
      if (!cancelled) setBins(results.flat());
    });
    return () => {
      cancelled = true;
    };
  }, [mode, sourceDoc, companyId]);

  // -------- GRPOs already created against the selected PO --------
  const loadGrpos = useCallback(() => {
    if (mode !== 'Receiving' || !sourceDoc) {
      setGrpos([]);
      return;
    }
    apiService
      .getWarehousePoGrpos(companyId, sourceDoc.docEntry)
      .then((r: any) => setGrpos(Array.isArray(r?.data) ? (r.data as GrpoDoc[]) : []))
      .catch(() => setGrpos([]));
  }, [mode, sourceDoc, companyId]);

  useEffect(() => {
    loadGrpos();
  }, [loadGrpos]);

  // When the GRPO viewer opens, enrich each GRPO with its FULL document from live
  // SAP (the synced list returns empty batch/expiry/bin — SAP populates those only
  // on a single-document GET). Falls back to the synced header if a fetch fails.
  useEffect(() => {
    if (!viewGrpoOpen || !sourceDoc || grpos.length === 0) {
      setGrpoDetails([]);
      return;
    }
    let cancelled = false;
    setGrpoDetailLoading(true);
    Promise.all(
      grpos.map((g) =>
        apiService
          .getWarehouseGrpoDetail(companyId, g.docEntry, sourceDoc.docEntry)
          .then((r: any) => (r?.data ? (r.data as GrpoDoc) : g))
          .catch(() => g)
      )
    ).then((results) => {
      if (!cancelled) {
        setGrpoDetails(results);
        setGrpoDetailLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [viewGrpoOpen, grpos, sourceDoc, companyId]);

  // -------- Deliveries already created against the selected SO --------
  const loadDeliveries = useCallback(() => {
    if (mode !== 'Delivery' || !sourceDoc) {
      setDeliveries([]);
      return;
    }
    apiService
      .getWarehouseSoDeliveries(companyId, sourceDoc.docEntry)
      .then((r: any) => setDeliveries(Array.isArray(r?.data) ? (r.data as DeliveryDoc[]) : []))
      .catch(() => setDeliveries([]));
  }, [mode, sourceDoc, companyId]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  // Enrich deliveries with their FULL document from live SAP when the viewer opens
  // (synced list returns empty batch/bin). Falls back to the synced header on error.
  useEffect(() => {
    if (!viewDeliveryOpen || !sourceDoc || deliveries.length === 0) {
      setDeliveryDetails([]);
      return;
    }
    let cancelled = false;
    setDeliveryDetailLoading(true);
    Promise.all(
      deliveries.map((d) =>
        apiService
          .getWarehouseDeliveryDetail(companyId, d.docEntry, sourceDoc.docEntry)
          // Keep the ledger's invoice link (live detail doesn't carry it).
          .then((r: any) => (r?.data ? ({ ...(r.data as DeliveryDoc), invoiceDocNum: d.invoiceDocNum, invoiceDocEntry: d.invoiceDocEntry }) : d))
          .catch(() => d)
      )
    ).then((results) => {
      if (!cancelled) {
        setDeliveryDetails(results);
        setDeliveryDetailLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [viewDeliveryOpen, deliveries, sourceDoc, companyId]);

  // -------- auto-retry queued imports on reconnect --------
  const processRetryQueue = useCallback(async () => {
    const queue = await loadRetryQueue();
    for (const batch of queue) {
      try {
        await apiService.importLotEntries(batch.companyId, batch.rows);
        await dequeueRetry(batch.id);
        setToast({ severity: 'success', message: `Queued import of ${batch.rows.length} rows synced.` });
      } catch {
        // Persist an incremented attempt count; drop after repeated failures so
        // a permanently-rejected payload can't retry forever.
        const updated = { ...batch, attempts: batch.attempts + 1 };
        if (updated.attempts > 5) await dequeueRetry(batch.id);
        else await enqueueRetry(updated);
      }
    }
  }, []);

  useEffect(() => {
    const onOnline = () => void processRetryQueue();
    window.addEventListener('online', onOnline);
    void processRetryQueue();
    return () => window.removeEventListener('online', onOnline);
  }, [processRetryQueue]);

  // -------- item resolution (live sync layer, then seed fallback) --------
  const resolveItem = useCallback(
    async (barcode?: string, gtin?: string): Promise<{ resolved: ResolvedItem | null; resolution: ScanResolution }> => {
      try {
        const res: any = await apiService.resolveScan(companyId, { barcode, gtin });
        if (res?.data?.resolved) {
          const resolution = (res.data.resolution as ScanResolution) || 'matched';
          return { resolved: res.data.resolved as ResolvedItem, resolution };
        }
      } catch {
        /* offline / error — fall through to seed */
      }
      const seed = resolveFromSeed(barcode, gtin);
      return { resolved: seed, resolution: seed ? 'matched' : 'unresolved' };
    },
    [companyId]
  );

  // Attach a resolved item to a document line; flag not-on-doc / over-qty.
  const matchToDocument = useCallback(
    (entry: ScanEntry): ScanEntry => {
      if (!sourceDoc || !entry.itemNo) return { ...entry, notOnDocument: !!sourceDoc };
      const line = sourceDoc.lines.find((l) => l.itemCode === entry.itemNo);
      if (!line) return { ...entry, notOnDocument: true, sourceDocLineNum: undefined };
      // Over-qty is checked against the SUM of received quantities on this line
      // (qty is now editable per scan), not the number of scans.
      const otherQty = entries
        .filter((e) => e.id !== entry.id && e.sourceDocEntry === sourceDoc.docEntry && e.sourceDocLineNum === line.lineNum)
        .reduce((sum, e) => sum + (Number(e.qty) || 1), 0);
      const thisQty = Number(entry.qty) || 1;
      return {
        ...entry,
        notOnDocument: false,
        sourceDocType: sourceDoc.docType,
        sourceDocNo: sourceDoc.docNum,
        sourceDocEntry: sourceDoc.docEntry,
        sourceDocLineNum: line.lineNum,
        overQty: line.openQty > 0 && otherQty + thisQty > line.openQty,
      };
    },
    [sourceDoc, entries]
  );

  // -------- decode handler --------
  const handleDecode = useCallback(
    async (text: string, kind: ScanKind) => {
      if (kind === '1d') {
        setPendingBarcode(text.trim());
        setToast({ severity: 'info', message: `1D barcode captured — now scan the DataMatrix (or scan another barcode).` });
        return;
      }
      // 2D — GS1 DataMatrix
      const parsed = parseGs1(text);
      const barcode = pendingBarcode ?? undefined;
      const { resolved, resolution } = await resolveItem(barcode, parsed.gtin);
      const base: ScanEntry = {
        id: localId(),
        mode,
        barcode,
        gtin: parsed.gtin,
        batchLot: parsed.batchLot,
        mfgDate: parsed.mfgDate,
        mfgDateIso: parsed.mfgDateIso,
        expDate: parsed.expDate,
        expDateIso: parsed.expDateIso,
        serialNumber: parsed.serialNumber,
        itemNo: resolved?.itemNo,
        itemDescription: resolved?.itemDescription,
        itemGroup: resolved?.itemGroup,
        pic: resolved?.pic,
        resolution,
        dedupeKey: scanDedupeKey(parsed),
        scannedAt: new Date().toISOString(),
        scannedBy: userName,
        errors: parsed.errors.length ? parsed.errors : undefined,
      };
      setDraft(matchToDocument(base));
      setPendingBarcode(null);
    },
    [pendingBarcode, mode, resolveItem, matchToDocument, userName]
  );

  // -------- session list ops --------
  const isDuplicate = useCallback(
    (d: ScanEntry) => modeEntries.some((e) => e.id !== d.id && e.dedupeKey === d.dedupeKey && d.dedupeKey !== '|'),
    [modeEntries]
  );

  const commitEntry = useCallback(async (entry: ScanEntry) => {
    await putEntry(entry);
    setEntries((prev) => {
      const without = prev.filter((e) => e.id !== entry.id);
      return [...without, entry];
    });
  }, []);

  const addDraft = useCallback(async () => {
    if (!draft) return;
    await commitEntry(draft);
    setToast({ severity: 'success', message: 'Added to session list.' });
    setDraft(null);
  }, [draft, commitEntry]);

  const deleteEntry = useCallback(async (id: string) => {
    await dbDeleteEntry(id);
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const onResolved = useCallback(
    async (entryId: string, patch: Partial<ScanEntry>) => {
      const target = entries.find((e) => e.id === entryId);
      if (!target) return;
      const updated = matchToDocument({ ...target, ...patch });
      await commitEntry(updated);
      setToast({ severity: 'success', message: 'Scan resolved.' });
    },
    [entries, matchToDocument, commitEntry]
  );

  // -------- exports --------
  const sessionMeta = useCallback(
    (): ScanSessionMeta => ({
      mode,
      user: userName,
      companyId,
      sourceDocType: sourceDoc?.docType,
      sourceDocNo: sourceDoc?.docNum,
      startedAt,
      count: modeEntries.length,
    }),
    [mode, userName, companyId, sourceDoc, startedAt, modeEntries.length]
  );

  const doExportXlsx = useCallback(() => {
    setExportAnchor(null);
    const built = exportXlsx(modeEntries, sessionMeta());
    setToast({ severity: 'success', message: `Exported ${built.resolvedCount} rows (+${built.exceptionCount} exceptions).` });
  }, [modeEntries, sessionMeta]);

  const doExportCsv = useCallback(() => {
    setExportAnchor(null);
    const n = exportCsv(modeEntries, sessionMeta());
    setToast({ severity: 'success', message: `Exported ${n} rows to CSV.` });
  }, [modeEntries, sessionMeta]);

  const doImport = useCallback(async () => {
    const eligible = modeEntries.filter((e) => e.resolution === 'matched' || e.resolution === 'mapped');
    if (eligible.length === 0) {
      setToast({ severity: 'warning', message: 'No resolved rows to import (matched/mapped only).' });
      return;
    }
    const rows = eligible.map((e) => ({
      itemNo: e.itemNo,
      itemDescription: e.itemDescription,
      itemGroup: e.itemGroup,
      pic: e.pic ?? '',
      gtin: e.gtin,
      batchLot: e.batchLot,
      mfgDate: e.mfgDate,
      mfgDateIso: e.mfgDateIso,
      expDate: e.expDate,
      expDateIso: e.expDateIso,
      serialNumber: e.serialNumber,
      resolution: e.resolution,
      sourceDocType: e.sourceDocType,
      sourceDocNo: e.sourceDocNo,
      sourceDocEntry: e.sourceDocEntry,
      sourceDocLineNum: e.sourceDocLineNum,
      mode: e.mode,
      scannedBy: e.scannedBy,
      scannedAt: e.scannedAt,
      idempotencyKey: e.dedupeKey,
    }));
    setImporting(true);
    try {
      const res: any = await apiService.importLotEntries(companyId, rows);
      const m = res?.meta ?? {};
      setToast({
        severity: 'success',
        message: `Imported: ${m.created ?? 0} created, ${m.duplicate ?? 0} duplicate, ${m.error ?? 0} error.`,
      });
    } catch (e: any) {
      // Offline / server unreachable — queue for auto-retry on reconnect.
      await enqueueRetry({ id: localId(), companyId, rows, queuedAt: new Date().toISOString(), attempts: 0 });
      setToast({ severity: 'warning', message: 'No connection — import queued and will auto-retry on reconnect.' });
    } finally {
      setImporting(false);
    }
  }, [modeEntries, companyId]);

  const syncBarcodes = useCallback(async () => {
    try {
      const res: any = await apiService.syncWarehouseBarcodes(companyId);
      const s = res?.data ?? {};
      setToast({
        severity: s.enabled ? 'success' : 'info',
        message: s.enabled
          ? `OBCD sync: ${s.synced} synced, ${s.failed} failed.`
          : `OBCD write-back is disabled on the server (${s.skipped} pending links saved locally).`,
      });
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Barcode sync failed.' });
    }
  }, [companyId]);

  const clearSession = useCallback(async () => {
    await clearEntries();
    setEntries([]);
    setToast({ severity: 'info', message: 'Session cleared.' });
  }, []);

  // -------- Submit to SAP as GRPO --------
  // Bins relevant to the in-review draft (filtered to its matched line's warehouse).
  const draftBins = useMemo(() => {
    if (!draft || !sourceDoc) return [];
    const line = sourceDoc.lines.find((l) => l.lineNum === draft.sourceDocLineNum);
    if (!line?.warehouseCode) return bins;
    return bins.filter((b) => b.warehouse === line.warehouseCode);
  }, [draft, sourceDoc, bins]);

  // Remaining receivable qty on the draft's matched PO line (open qty minus what's
  // already in this session for that line) — hard cap enforced in the review card.
  const draftMaxQty = useMemo(() => {
    if (!draft || !sourceDoc || draft.sourceDocLineNum == null) return undefined;
    const line = sourceDoc.lines.find((l) => l.lineNum === draft.sourceDocLineNum);
    if (!line) return undefined; // not anchored to a PO line → no cap
    const others = entries
      .filter((e) => e.id !== draft.id && e.sourceDocEntry === sourceDoc.docEntry && e.sourceDocLineNum === line.lineNum)
      .reduce((s, e) => s + (Number(e.qty) || 1), 0);
    return Math.max(0, line.openQty - others);
  }, [draft, sourceDoc, entries]);

  // Confirmed, PO-anchored rows for the currently selected PO.
  const grpoEligible = useMemo(() => {
    if (mode !== 'Receiving' || !sourceDoc) return [];
    return modeEntries.filter(
      (e) =>
        (e.resolution === 'matched' || e.resolution === 'mapped') &&
        e.sourceDocEntry === sourceDoc.docEntry &&
        e.sourceDocLineNum != null &&
        !!e.itemNo &&
        (Number(e.qty) || 1) > 0
    );
  }, [mode, sourceDoc, modeEntries]);

  // Aggregated preview: one row per (PO line + item + batch + bin), qty summed.
  const grpoPreview = useMemo(() => {
    const map = new Map<string, { itemNo: string; batchLot?: string; expDateIso?: string; binCode?: string; qty: number }>();
    for (const e of grpoEligible) {
      const qty = Number(e.qty) || 1;
      const key = [e.sourceDocLineNum, e.itemNo, e.batchLot ?? '', e.binAbsEntry ?? ''].join('|');
      const cur = map.get(key);
      if (cur) cur.qty += qty;
      else map.set(key, { itemNo: e.itemNo as string, batchLot: e.batchLot, expDateIso: e.expDateIso, binCode: e.binCode, qty });
    }
    return Array.from(map.values());
  }, [grpoEligible]);

  const skippedCount = modeEntries.length - grpoEligible.length;
  const binsRequiredMissing = bins.length > 0 && grpoEligible.some((e) => e.binAbsEntry == null);
  const grpoHasOverQty = grpoEligible.some((e) => e.overQty);

  const submitGrpo = useCallback(async () => {
    if (!sourceDoc || grpoEligible.length === 0) return;
    const lines = grpoEligible.map((e) => {
      const line = sourceDoc.lines.find((l) => l.lineNum === e.sourceDocLineNum);
      return {
        itemCode: e.itemNo,
        sourceDocLineNum: e.sourceDocLineNum,
        qty: Number(e.qty) || 1,
        warehouseCode: line?.warehouseCode,
        batchLot: e.batchLot,
        expDateIso: e.expDateIso,
        mfgDateIso: e.mfgDateIso,
        binCode: e.binCode,
        binAbsEntry: e.binAbsEntry,
      };
    });
    setSubmitting(true);
    try {
      const res: any = await apiService.submitWarehouseGrpo(companyId, sourceDoc.docEntry, lines);
      const d = res?.data ?? {};
      const base = d.reconciled
        ? `This receipt already exists in SAP (GRPO ${d.docNum}).`
        : `GRPO ${d.docNum} created in SAP.`;
      setToast({
        severity: 'success',
        // The backend pulls the new GRPO straight back into the local cache, so it
        // appears in the received-GRPOs list right away. If that pull-back failed
        // (d.synced === false) it will catch up on the next scheduled SAP sync.
        message: d.synced === false ? `${base} It will appear once the next SAP sync runs.` : `${base} You can now view its details.`,
      });
      // Remove the submitted rows from the session so they can't be double-posted.
      const submittedIds = new Set(grpoEligible.map((e) => e.id));
      for (const id of submittedIds) await dbDeleteEntry(id);
      setEntries((prev) => prev.filter((e) => !submittedIds.has(e.id)));
      setGrpoOpen(false);
      loadGrpos(); // backend already refreshed the local sap-grpo cache, so this shows the new GRPO immediately
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'GRPO submission failed.' });
    } finally {
      setSubmitting(false);
    }
  }, [sourceDoc, grpoEligible, companyId, loadGrpos]);

  // -------- Submit to SAP as Delivery (mirror of GRPO, for the SO) --------
  const deliveryEligible = useMemo(() => {
    if (mode !== 'Delivery' || !sourceDoc) return [];
    return modeEntries.filter(
      (e) =>
        (e.resolution === 'matched' || e.resolution === 'mapped') &&
        e.sourceDocEntry === sourceDoc.docEntry &&
        e.sourceDocLineNum != null &&
        !!e.itemNo &&
        (Number(e.qty) || 1) > 0
    );
  }, [mode, sourceDoc, modeEntries]);

  const deliveryPreview = useMemo(() => {
    const map = new Map<string, { itemNo: string; warehouse?: string; batchLot?: string; expDateIso?: string; binCode?: string; qty: number }>();
    for (const e of deliveryEligible) {
      const qty = Number(e.qty) || 1;
      const warehouse = sourceDoc?.lines.find((l) => l.lineNum === e.sourceDocLineNum)?.warehouseCode;
      const key = [e.sourceDocLineNum, e.itemNo, e.batchLot ?? '', e.binAbsEntry ?? ''].join('|');
      const cur = map.get(key);
      if (cur) cur.qty += qty;
      else map.set(key, { itemNo: e.itemNo as string, warehouse, batchLot: e.batchLot, expDateIso: e.expDateIso, binCode: e.binCode, qty });
    }
    return Array.from(map.values());
  }, [deliveryEligible, sourceDoc]);

  const deliverySkipped = modeEntries.length - deliveryEligible.length;
  const deliveryBinsMissing = bins.length > 0 && deliveryEligible.some((e) => e.binAbsEntry == null);
  const deliveryHasOverQty = deliveryEligible.some((e) => e.overQty);

  // Deliverability (availability + expiry) for the eligible lines — fetched when
  // the dialog opens. SAP enforces bin-exact qty; this is the proactive guard.
  useEffect(() => {
    if (!deliveryOpen || deliveryEligible.length === 0) {
      setDeliveryStock([]);
      return;
    }
    let cancelled = false;
    const lines = deliveryPreview.map((r) => ({ itemCode: r.itemNo, warehouse: r.warehouse, batch: r.batchLot }));
    apiService
      .getDeliveryStock(companyId, lines)
      .then((r: any) => { if (!cancelled) setDeliveryStock(Array.isArray(r?.data) ? (r.data as DeliveryStockRow[]) : []); })
      .catch(() => { if (!cancelled) setDeliveryStock([]); });
    return () => { cancelled = true; };
  }, [deliveryOpen, deliveryEligible, deliveryPreview, companyId]);

  // Look up the deliverability row for a preview line.
  const stockFor = useCallback(
    (itemNo: string, warehouse?: string, batchLot?: string): DeliveryStockRow | undefined =>
      deliveryStock.find(
        (s) => s.itemCode === itemNo && (s.warehouse ?? '') === (warehouse ?? '') && (s.batch ?? '') === (batchLot ?? '')
      ),
    [deliveryStock]
  );

  // Expired = synced batch expiry (sap-item-expiry) OR the scanned EXP (GS1 AI 17),
  // since the synced expiry table is sparse.
  const today = new Date().toISOString().slice(0, 10);
  const isPreviewExpired = useCallback(
    (r: { itemNo: string; warehouse?: string; batchLot?: string; expDateIso?: string }): boolean => {
      const s = stockFor(r.itemNo, r.warehouse, r.batchLot);
      if (s?.expired) return true;
      return !!r.expDateIso && String(r.expDateIso).slice(0, 10) < today;
    },
    [stockFor, today]
  );
  const deliveryHasExpired = deliveryPreview.some((r) => isPreviewExpired(r));
  // Sum issued per item+warehouse and compare to on-hand.
  const deliveryOverAvailable = useMemo(() => {
    const issued = new Map<string, number>();
    for (const r of deliveryPreview) issued.set(`${r.itemNo}|${r.warehouse ?? ''}`, (issued.get(`${r.itemNo}|${r.warehouse ?? ''}`) || 0) + r.qty);
    for (const [k, total] of issued) {
      const [itemNo, wh] = k.split('|');
      const s = deliveryStock.find((x) => x.itemCode === itemNo && (x.warehouse ?? '') === wh);
      if (s && s.onHand != null && total > s.onHand + 1e-9) return true;
    }
    return false;
  }, [deliveryPreview, deliveryStock]);

  const submitDelivery = useCallback(async () => {
    if (!sourceDoc || deliveryEligible.length === 0) return;
    const lines = deliveryEligible.map((e) => {
      const line = sourceDoc.lines.find((l) => l.lineNum === e.sourceDocLineNum);
      return {
        itemCode: e.itemNo,
        sourceDocLineNum: e.sourceDocLineNum,
        qty: Number(e.qty) || 1,
        warehouseCode: line?.warehouseCode,
        batchLot: e.batchLot,
        expDateIso: e.expDateIso,
        mfgDateIso: e.mfgDateIso,
        binCode: e.binCode,
        binAbsEntry: e.binAbsEntry,
      };
    });
    setSubmitting(true);
    try {
      const res: any = await apiService.submitWarehouseDelivery(companyId, sourceDoc.docEntry, lines, createInvoice);
      const d = res?.data ?? {};
      const base = d.reconciled ? `This delivery already exists in SAP (Delivery ${d.docNum}).` : `Delivery ${d.docNum} created in SAP.`;
      let invMsg = '';
      if (createInvoice) {
        if (d.invoice?.docNum) invMsg = ` AR Invoice ${d.invoice.docNum} created.`;
        else if (d.invoiceError) invMsg = ` AR Invoice failed: ${String(d.invoiceError).slice(0, 160)}`;
      }
      // Backend pulls the new Delivery (+ Invoice) straight back into the local
      // cache so they show immediately; if a pull-back failed, say so.
      const syncLagged = d.synced === false || (d.invoice && d.invoice.synced === false);
      const syncMsg = syncLagged ? ' It will appear once the next SAP sync runs.' : '';
      setToast({
        severity: createInvoice && d.invoiceError ? 'warning' : 'success',
        message: `${base}${invMsg}${syncMsg}`.trim(),
      });
      // Remove the submitted rows from the session so they can't be double-posted.
      const submittedIds = new Set(deliveryEligible.map((e) => e.id));
      for (const id of submittedIds) await dbDeleteEntry(id);
      setEntries((prev) => prev.filter((e) => !submittedIds.has(e.id)));
      setDeliveryOpen(false);
      loadDeliveries(); // backend already refreshed the local sap-deliveries cache
    } catch (e: any) {
      setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Delivery submission failed.' });
    } finally {
      setSubmitting(false);
    }
  }, [sourceDoc, deliveryEligible, companyId, createInvoice, loadDeliveries]);

  // -------- Printable PDFs (GRPO / Delivery Order / AR Invoice) --------
  const openPdf = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (!w) {
      // Popup blocked (common on mobile) — fall back to a download.
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }, []);

  const runPrint = useCallback(
    async (key: string, filename: string, fetcher: () => Promise<Blob>) => {
      setPrintingKey(key);
      try {
        openPdf(await fetcher(), filename);
      } catch (e: any) {
        setToast({ severity: 'error', message: e?.response?.data?.message ?? 'Could not generate the PDF.' });
      } finally {
        setPrintingKey(null);
      }
    },
    [openPdf]
  );

  const printGrpo = useCallback(
    (docEntry: number) =>
      runPrint(`grpo-${docEntry}`, `MRV-${docEntry}.pdf`, () =>
        apiService.getWarehouseGrpoPdf(companyId, docEntry, sourceDoc?.docEntry)
      ),
    [runPrint, companyId, sourceDoc]
  );
  const printDelivery = useCallback(
    (docEntry: number) =>
      runPrint(`dlv-${docEntry}`, `DeliveryOrder-${docEntry}.pdf`, () =>
        apiService.getWarehouseDeliveryPdf(companyId, docEntry, sourceDoc?.docEntry)
      ),
    [runPrint, companyId, sourceDoc]
  );
  const printInvoice = useCallback(
    (invDocEntry: number, deliveryDate?: string) =>
      runPrint(`inv-${invDocEntry}`, `AR-Invoice-${invDocEntry}.pdf`, () =>
        apiService.getWarehouseInvoicePdf(companyId, invDocEntry, { soDocNum: sourceDoc?.docNum, deliveryDate })
      ),
    [runPrint, companyId, sourceDoc]
  );

  if (!companyId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select a company to begin warehouse scanning.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Inventory2 color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Warehouse Receiving / Delivery
        </Typography>
        {pendingBarcode && <Chip color="info" label={`1D barcode ready: ${pendingBarcode}`} onDelete={() => setPendingBarcode(null)} />}
      </Stack>

      <Tabs value={mode} onChange={(_e, v) => setMode(v)} sx={{ mb: 2 }}>
        <Tab value="Receiving" label="Receiving (GRPO · Purchase Order)" />
        <Tab value="Delivery" label="Delivery (Goods Issue · Sales Order)" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <SourceDocumentPicker companyId={companyId} mode={mode} selected={sourceDoc} onSelect={setSourceDoc} />
      </Paper>

      {sourceDoc && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2">Document progress</Typography>
            <Box sx={{ flex: 1 }} />
            {mode === 'Receiving' && grpos.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<Receipt />} onClick={() => setViewGrpoOpen(true)}>
                {grpos.length} GRPO{grpos.length > 1 ? 's' : ''} received — view
              </Button>
            )}
            {mode === 'Delivery' && deliveries.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<LocalShipping />} onClick={() => setViewDeliveryOpen(true)}>
                {deliveries.length} Deliver{deliveries.length > 1 ? 'ies' : 'y'} — view
              </Button>
            )}
          </Stack>
          <DocumentProgress doc={sourceDoc} entries={modeEntries} />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Scan
        </Typography>
        <ScannerView
          onDecode={handleDecode}
          disabled={!!draft}
          prompt={
            pendingBarcode
              ? 'Now scan the GS1 DataMatrix for this item.'
              : 'Scan the 1D barcode first, then the GS1 DataMatrix (or scan the DataMatrix alone).'
          }
        />
      </Paper>

      {draft && (
        <Box sx={{ mb: 2 }}>
          <ScanReviewCard
            draft={draft}
            bins={draftBins}
            maxQty={draftMaxQty}
            duplicate={isDuplicate(draft)}
            onChange={(patch) => setDraft((d) => (d ? matchToDocument({ ...d, ...patch }) : d))}
            onAdd={addDraft}
            onCancel={() => setDraft(null)}
            onNeedsMapping={() => {
              setResolveEntry(draft);
            }}
          />
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1">Session list</Typography>
          <Chip size="small" label={`${modeEntries.length} scans`} />
          {needsMappingCount > 0 && <Chip size="small" color="warning" label={`${needsMappingCount} need mapping`} />}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="contained"
            startIcon={<Download />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            disabled={modeEntries.length === 0}
          >
            Export to Excel
          </Button>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={doExportXlsx}>Excel (.xlsx) — Item Master + Exceptions</MenuItem>
            <MenuItem onClick={doExportCsv}>CSV (resolved rows)</MenuItem>
          </Menu>
          <Button
            size="small"
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <CloudUpload />}
            onClick={doImport}
            disabled={modeEntries.length === 0 || importing}
          >
            Import to Item Master
          </Button>
          <Button size="small" variant="text" startIcon={<Sync />} onClick={syncBarcodes}>
            Sync barcodes
          </Button>
          {mode === 'Receiving' && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<Publish />}
              onClick={() => setGrpoOpen(true)}
              disabled={!sourceDoc || grpoEligible.length === 0}
            >
              Submit to SAP as GRPO
            </Button>
          )}
          {mode === 'Delivery' && (
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={<LocalShipping />}
              onClick={() => setDeliveryOpen(true)}
              disabled={!sourceDoc || deliveryEligible.length === 0}
            >
              Submit to SAP as Delivery
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 1.5 }} />
        <SessionTable
          entries={modeEntries}
          onEdit={(entry) => setDraft(entry)}
          onDelete={deleteEntry}
          onResolve={(entry) => setResolveEntry(entry)}
        />
        {modeEntries.length > 0 && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button size="small" color="error" onClick={clearSession}>
              Clear session
            </Button>
          </Stack>
        )}
      </Paper>

      <ResolveQueueDialog
        open={!!resolveEntry}
        companyId={companyId}
        entry={resolveEntry}
        onClose={() => setResolveEntry(null)}
        onResolved={(id, patch) => {
          // If resolving the in-review draft, update it; else update the listed entry.
          if (draft && draft.id === id) {
            setDraft((d) => (d ? matchToDocument({ ...d, ...patch }) : d));
          } else {
            void onResolved(id, patch);
          }
        }}
      />

      <ResponsiveDialog open={grpoOpen} onClose={() => { if (!submitting) setGrpoOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Submit GRPO to SAP</DialogTitle>
        <DialogContent dividers>
          {sourceDoc && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Goods Receipt PO against <strong>PO {sourceDoc.docNum}</strong> — {sourceDoc.cardName}
            </Typography>
          )}
          {grpoPreview.length === 0 ? (
            <Alert severity="warning">No eligible rows to post. Rows must be matched/mapped and anchored to this PO.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Bin</TableCell>
                  <TableCell align="right">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grpoPreview.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.itemNo}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.batchLot || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.expDateIso || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{r.binCode || '—'}</TableCell>
                    <TableCell align="right">{r.qty}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {skippedCount > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              {skippedCount} row(s) excluded (needs mapping, not on this PO, or zero qty).
            </Alert>
          )}
          {binsRequiredMissing && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              This warehouse is bin-managed but some rows have no bin selected — SAP may reject those lines.
            </Alert>
          )}
          {grpoHasOverQty && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              One or more rows exceed the open PO quantity. You cannot receive more than the PO quantity — fix those rows before posting.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrpoOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={submitGrpo}
            disabled={submitting || grpoPreview.length === 0 || grpoHasOverQty}
            startIcon={submitting ? <CircularProgress size={16} /> : <Publish />}
          >
            {submitting ? 'Posting…' : 'Post GRPO'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={viewGrpoOpen} onClose={() => setViewGrpoOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Goods Receipt PO{sourceDoc ? ` — against PO ${sourceDoc.docNum}` : ''}</DialogTitle>
        <DialogContent dividers>
          {grpoDetailLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading batch / expiry detail from SAP…</Typography>
            </Stack>
          )}
          {grpos.length === 0 ? (
            <Alert severity="info">No GRPOs found for this PO.</Alert>
          ) : (
            <Stack spacing={2.5}>
              {(grpoDetails.length ? grpoDetails : grpos).map((g) => (
                <Paper key={g.docEntry} variant="outlined" sx={{ p: 2 }}>
                  {/* Header section — SAP GRPO document style */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Receipt color="primary" fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      GRPO {g.docNum}
                    </Typography>
                    {g.docStatus && <Chip size="small" label={g.docStatus} />}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={printingKey === `grpo-${g.docEntry}` ? <CircularProgress size={14} /> : <Print />}
                      onClick={() => printGrpo(g.docEntry)}
                      disabled={printingKey === `grpo-${g.docEntry}`}
                    >
                      Print
                    </Button>
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="body2"><strong>Vendor:</strong> {g.cardCode} — {g.cardName}</Typography>
                    <Typography variant="body2"><strong>Posting date:</strong> {g.docDate ? String(g.docDate).slice(0, 10) : '—'}</Typography>
                    <Typography variant="body2"><strong>Total:</strong> {g.docTotal != null ? `${g.docTotal.toLocaleString()} ${g.docCurrency || ''}`.trim() : '—'}</Typography>
                    {g.numAtCard && <Typography variant="body2"><strong>Ref (NumAtCard):</strong> {g.numAtCard}</Typography>}
                    {g.comments && <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}><strong>Remarks:</strong> {g.comments}</Typography>}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {/* Details section */}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Whse</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {g.lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{l.itemCode}</TableCell>
                          <TableCell>{l.itemDescription}</TableCell>
                          <TableCell align="right">{l.quantity}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{l.warehouseCode || '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {l.batches.length ? l.batches.map((b) => b.batchNumber).join(', ') : '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {l.batches.length ? l.batches.map((b) => (b.expiryDate ? String(b.expiryDate).slice(0, 10) : '—')).join(', ') : '—'}
                          </TableCell>
                          <TableCell align="right">{l.lineTotal != null ? l.lineTotal.toLocaleString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewGrpoOpen(false)}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* --- Submit Delivery dialog --- */}
      <ResponsiveDialog open={deliveryOpen} onClose={() => { if (!submitting) setDeliveryOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Submit Delivery to SAP</DialogTitle>
        <DialogContent dividers>
          {sourceDoc && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Delivery against <strong>SO {sourceDoc.docNum}</strong> — {sourceDoc.cardName}
            </Typography>
          )}
          {deliveryPreview.length === 0 ? (
            <Alert severity="warning">No eligible rows to post. Rows must be matched/mapped and anchored to this SO.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Whse</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Bin</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">On hand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveryPreview.map((r, i) => {
                  const s = stockFor(r.itemNo, r.warehouse, r.batchLot);
                  const expired = isPreviewExpired(r);
                  const binMissing = bins.length > 0 && !r.binCode;
                  return (
                    <TableRow key={i} sx={expired ? { bgcolor: 'rgba(211,47,47,0.08)' } : undefined}>
                      <TableCell>{r.itemNo}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.warehouse || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.batchLot || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {(s?.expiryDate || r.expDateIso) ? String(s?.expiryDate || r.expDateIso).slice(0, 10) : '—'}
                        {expired && <Chip size="small" color="error" label="expired" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: binMissing ? 'error.main' : undefined }}>
                        {r.binCode || (binMissing ? 'select bin' : '—')}
                      </TableCell>
                      <TableCell align="right">{r.qty}</TableCell>
                      <TableCell align="right" sx={s && s.onHand != null && r.qty > s.onHand ? { color: 'error.main', fontWeight: 700 } : undefined}>
                        {s?.onHand != null ? s.onHand : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={<Checkbox checked={createInvoice} onChange={(e) => setCreateInvoice(e.target.checked)} />}
            label="Also create the AR Invoice (Sales Order → Delivery → AR Invoice)"
          />
          {deliverySkipped > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {deliverySkipped} row(s) excluded (needs mapping, not on this SO, or zero qty).
            </Alert>
          )}
          {deliveryBinsMissing && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              This warehouse is bin-managed — every row must have a bin selected. Pick a bin on each scan (Review scan → Bin location) before posting.
            </Alert>
          )}
          {deliveryHasExpired && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              One or more batches are expired and cannot be delivered. Remove the expired rows before posting.
            </Alert>
          )}
          {deliveryOverAvailable && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              One or more rows exceed the on-hand stock in the warehouse. Reduce the quantity (bin-level availability is also enforced by SAP).
            </Alert>
          )}
          {deliveryHasOverQty && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              One or more rows exceed the open SO quantity. You cannot deliver more than the ordered quantity — fix those rows before posting.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveryOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={submitDelivery}
            disabled={submitting || deliveryPreview.length === 0 || deliveryHasOverQty || deliveryHasExpired || deliveryOverAvailable || deliveryBinsMissing}
            startIcon={submitting ? <CircularProgress size={16} /> : <LocalShipping />}
          >
            {submitting ? 'Posting…' : createInvoice ? 'Post Delivery + Invoice' : 'Post Delivery'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* --- Deliveries viewer --- */}
      <ResponsiveDialog open={viewDeliveryOpen} onClose={() => setViewDeliveryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Deliveries{sourceDoc ? ` — against SO ${sourceDoc.docNum}` : ''}</DialogTitle>
        <DialogContent dividers>
          {deliveryDetailLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading batch / bin detail from SAP…</Typography>
            </Stack>
          )}
          {deliveries.length === 0 ? (
            <Alert severity="info">No deliveries found for this SO.</Alert>
          ) : (
            <Stack spacing={2.5}>
              {(deliveryDetails.length ? deliveryDetails : deliveries).map((g) => (
                <Paper key={g.docEntry} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <LocalShipping color="primary" fontSize="small" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Delivery {g.docNum}
                    </Typography>
                    {g.docStatus && <Chip size="small" label={g.docStatus} />}
                    {g.invoiceDocNum && (
                      <Chip size="small" color="success" icon={<ReceiptLong />} label={`AR Invoice ${g.invoiceDocNum}`} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={printingKey === `dlv-${g.docEntry}` ? <CircularProgress size={14} /> : <Print />}
                      onClick={() => printDelivery(g.docEntry)}
                      disabled={printingKey === `dlv-${g.docEntry}`}
                    >
                      Print Delivery Order
                    </Button>
                    {g.invoiceDocEntry != null && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={printingKey === `inv-${g.invoiceDocEntry}` ? <CircularProgress size={14} /> : <ReceiptLong />}
                        onClick={() => printInvoice(g.invoiceDocEntry as number, g.docDate)}
                        disabled={printingKey === `inv-${g.invoiceDocEntry}`}
                      >
                        Print AR Invoice
                      </Button>
                    )}
                  </Stack>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="body2"><strong>Customer:</strong> {g.cardCode} — {g.cardName}</Typography>
                    <Typography variant="body2"><strong>Posting date:</strong> {g.docDate ? String(g.docDate).slice(0, 10) : '—'}</Typography>
                    <Typography variant="body2"><strong>Total:</strong> {g.docTotal != null ? `${g.docTotal.toLocaleString()} ${g.docCurrency || ''}`.trim() : '—'}</Typography>
                    {g.numAtCard && <Typography variant="body2"><strong>Ref (NumAtCard):</strong> {g.numAtCard}</Typography>}
                    {g.comments && <Typography variant="body2" sx={{ gridColumn: '1 / -1' }}><strong>Remarks:</strong> {g.comments}</Typography>}
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Whse</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {g.lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{l.itemCode}</TableCell>
                          <TableCell>{l.itemDescription}</TableCell>
                          <TableCell align="right">{l.quantity}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{l.warehouseCode || '—'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {l.batches.length ? l.batches.map((b) => b.batchNumber).join(', ') : '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {l.batches.length ? l.batches.map((b) => (b.expiryDate ? String(b.expiryDate).slice(0, 10) : '—')).join(', ') : '—'}
                          </TableCell>
                          <TableCell align="right">{l.lineTotal != null ? l.lineTotal.toLocaleString() : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDeliveryOpen(false)}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
